<template>
  <el-dialog
    class="dialog-wrapper"
    visible
    :show-close="false"
    @close="abortCreate"
  >
    <ContentHeader
      slot="title"
      :title="
        isModalEdit
          ? $t('locationsManagement.create.editLocation')
          : $t('locationsManagement.create.createLocation')
      "
    />
    <ValidationObserver
      ref="recipientForm"
      v-slot="{ handleSubmit, invalid }"
    >
      <el-form
        ref="form"
        label-position="top"
      >
        <el-row
          :gutter="20"
          type="flex"
        >
          <el-col :span="8">
            <FormItem
              rules="required"
              :label="$t('locationsManagement.create.name')"
            >
              <el-input
                v-model="formData.name"
                :disabled="isModalEdit"
              />
            </FormItem>
          </el-col>
          <el-col :span="8">
            <FormItem
              rules="required"
              :label="$t('locationsManagement.create.description')"
            >
              <el-input v-model="formData.description" />
            </FormItem>
          </el-col>
        </el-row>

        <el-row
          :gutter="20"
          type="flex"
        >
          <el-col :span="8">
            <FormItem
              rules="required"
              :label="$t('locationsManagement.create.code')"
            >
              <el-input
                v-model="formData.code"
                :disabled="isModalEdit"
              />
            </FormItem>
          </el-col>
          <el-col :span="8">
            <FormItem
              rules="required"
              :label="$t('locationsManagement.create.enabled')"
            >
              <el-switch
                v-model="formData.enabled"
                :active-text="$t('locationsManagement.create.enabled')"
              >
              </el-switch>
            </FormItem>
          </el-col>
          <el-col :span="8">
            <FormItem
              rules="required"
              :label="$t('locationsManagement.create.aifaEnabled')"
            >
              <el-switch
                v-model="formData.aifaEnabled"
                :active-text="$t('locationsManagement.create.enabled')"
              >
              </el-switch>
            </FormItem>
          </el-col>
        </el-row>

        <el-row
          :gutter="20"
          type="flex"
        >
          <el-col :span="8">
            <FormItem
              rules="required"
              :label="$t('locationsManagement.create.sampleLabelPrinter')"
            >
              <el-select
                v-model="formData.sampleLabelPrinter"
                placeholder="Select Sample Label Printer"
              >
                <el-option
                  v-for="printer in printers"
                  :key="printer"
                  :label="printer"
                  :value="printer"
                />
              </el-select>
            </FormItem>
          </el-col>

          <el-col :span="8">
            <FormItem
              rules="required"
              :label="$t('locationsManagement.create.materialLabelPrinter')"
            >
              <el-select
                v-model="formData.materialLabelPrinter"
                placeholder="Select Material Label Printer"
              >
                <el-option
                  v-for="printer in printers"
                  :key="printer"
                  :label="printer"
                  :value="printer"
                />
              </el-select>
            </FormItem>
          </el-col>
        </el-row>

        <el-row :gutter="20">
          <el-col :span="24">
            <el-divider content-position="left">
              {{ $t('locationsManagement.create.emails') }}
            </el-divider>
            <div
              v-for="(email, index) in formData.emails"
              :key="index"
            >
              <FormItem
                :rules="'required|email'"
                :vid="'email_' + index"
                :label="$t('locationsManagement.create.emailPlaceholder')"
                label-width="0px"
                class="email-form-item"
              >
                <el-input
                  v-model="formData.emails[index]"
                  :placeholder="
                    $t('locationsManagement.create.emailPlaceholder')
                  "
                >
                  <el-button
                    slot="append"
                    type="danger"
                    icon="el-icon-delete"
                    @click.prevent="removeEmail(index)"
                  ></el-button>
                </el-input>
              </FormItem>
            </div>
            <el-button
              type="text"
              icon="el-icon-plus"
              @click.prevent="addEmail"
            >
              {{ $t('locationsManagement.create.addEmail') }}
            </el-button>
          </el-col>
        </el-row>

        <FormButtons>
          <el-button
            type="secondary"
            @click.prevent="abortCreate"
          >
            {{ $t('locationsManagement.create.abort') }}
          </el-button>
          <el-button
            type="primary"
            native-type="submit"
            :disabled="invalid || formData.emails.length === 0"
            @click.prevent="handleSubmit(createUpdateLocation)"
          >
            {{ $t('locationsManagement.create.save') }}
          </el-button>
        </FormButtons>
      </el-form>
    </ValidationObserver>
  </el-dialog>
</template>

<script>
  import { locationsManagementApi } from '@/api';
  import FormItem from '@/components/FormItem.vue';
  import ContentHeader from '@/components/ContentHeader.vue';
  import FormButtons from '@/components/FormButtons.vue';

  const defaultData = {
    name: '',
    description: '',
    code: '',
    enabled: true,
    aifaEnabled: false,
    sampleLabelPrinter: '', // Add for sample label printer
    materialLabelPrinter: '',
    emails: [''],
  };

  export default {
    name: 'CreateEditLocation',
    components: {
      FormItem,
      ContentHeader,
      FormButtons,
    },
    props: {
      initialData: { type: Object, default: () => defaultData },
      isModalEdit: { type: Boolean, default: false },
    },
    data() {
      return {
        formData: { ...defaultData },
        printers: [],
      };
    },

    async created() {
      try {
        const response = await locationsManagementApi.fetchPrinters();

        this.printers = response.data;

        if (this.isModalEdit) {
          const {
            name,
            description,
            code,
            enabled,
            aifaEnabled,
            sampleLabelPrinter,
            materialLabelPrinter,
          } = this.initialData;
          this.formData = {
            ...defaultData,
            name,
            description,
            code,
            enabled,
            aifaEnabled,
            sampleLabelPrinter,
            materialLabelPrinter,
            emails: this.initialData.locationEmails
              ? this.initialData.locationEmails.map((le) => le.email)
              : [],
          };
        }
      } catch (error) {
        this.$notify({
          showClose: true,
          type: 'error',
          message: error.response.data.error.message,
        });
      }
    },

    methods: {
      async createUpdateLocation() {
        try {
          if (this.isModalEdit) {
            const data = {
              description: this.formData.description,
              enabled: this.formData.enabled,
              aifaEnabled: this.formData.aifaEnabled,
              sampleLabelPrinter: this.formData.sampleLabelPrinter, // Post Sample Label Printer
              materialLabelPrinter: this.formData.materialLabelPrinter,
              emails: this.formData.emails,
            };
            await locationsManagementApi.patch(this.initialData.id, data);
          } else {
            await locationsManagementApi.create(this.formData);
          }
          this.$notify({
            showClose: true,
            type: 'success',
            message: this.$t('locationsManagement.create.success'),
          });
          this.$emit('dialogClose', { success: true });
        } catch (error) {
          this.$notify({
            showClose: true,
            type: 'error',
            message: error.response.data.error.message,
          });
        }
      },
      abortCreate() {
        this.$emit('dialogClose', { success: false });
      },
      addEmail() {
        this.formData.emails.push('');
      },
      removeEmail(index) {
        this.formData.emails.splice(index, 1);
      },
    },
  };
</script>

<style lang="scss" scoped>
  .el-divider--horizontal {
    .el-divider__text {
      background-color: $--dialog-background-color;
    }
  }
</style>
